/**
 * DocsMarkdown — markdown renderer scoped to the /docs/* pages.
 *
 * Extends the contract of NarrativeMarkdown with two differences:
 *   1. Inline raw HTML is allowed (via rehype-raw) so doc-only chrome
 *      (callouts, fact lists, tile grids, diagram figures) can be
 *      authored alongside prose. Safe because doc markdown is committed
 *      to the repo by engineers, not user-submitted.
 *   2. Headings step down one level less aggressively because the page
 *      itself owns the h1; markdown h2 → MUI h6 (mono uppercase) etc.
 *
 * NarrativeMarkdown stays untouched — its XSS guard remains for any
 * model-generated prose.
 */

import type { ReactNode } from 'react';
import {
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import 'highlight.js/styles/github.css';

const inlineCodeSx = {
  display: 'inline',
  px: 0.5,
  py: 0,
  mx: 0.25,
  bgcolor: 'rgba(255, 255, 255, 0.04)',
  color: 'primary.light',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 0.5,
  fontFamily:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
  fontSize: '0.85em',
  // Allow long identifiers / tuples to wrap rather than overflow a tile.
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
} as const;

const preWrapperSx = {
  m: 0,
  my: 2.5,
  p: 0,
  bgcolor: 'background.elev2',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'auto',
  '& code': {
    display: 'block',
    p: 2,
    bgcolor: 'transparent',
    border: 'none',
    borderRadius: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.6,
    whiteSpace: 'pre',
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
  },
  '& code.hljs': { background: 'transparent' },
} as const;

// Doc-only custom-class chrome. All classes are stable so authors can
// rely on them in markdown.
const proseWrapperSx = {
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-of-type': { mb: 0 },

  // .callout — amber-left-bordered notice block
  '& .callout': {
    my: 2.5,
    py: 1.5,
    pl: 2,
    pr: 2,
    bgcolor: 'primary.soft',
    borderLeft: '3px solid',
    borderColor: 'primary.main',
    borderRadius: 0.75,
  },
  '& .callout .label': {
    display: 'block',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    color: 'primary.main',
    fontWeight: 700,
    mb: 0.5,
  },
  '& .callout p': { my: 0.5 },

  // .fact-list — chevron-prefixed key/value list
  '& .fact-list': {
    listStyle: 'none',
    p: 0,
    my: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.elev2',
  },
  '& .fact-list li': {
    listStyle: 'none',
    px: 2,
    py: 1.25,
    borderBottom: '1px solid',
    borderColor: 'divider',
    fontSize: '0.875rem',
    lineHeight: 1.55,
  },
  '& .fact-list li:last-child': { borderBottom: 'none' },
  '& .fact-list li::before': {
    content: '"›"',
    color: 'primary.main',
    fontWeight: 700,
    mr: 1,
    fontFamily: '"JetBrains Mono", monospace',
  },

  // .tile-grid — responsive grid of tiles for the landing page
  '& .tile-grid': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 1.5,
    my: 2.5,
  },
  '& .tile-grid > *': {
    display: 'block',
    p: 2,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    color: 'text.primary',
    textDecoration: 'none',
    transition: 'border-color 180ms ease, transform 120ms ease',
  },
  '& .tile-grid a:hover, & .tile-grid > div:hover': {
    borderColor: 'primary.main',
    transform: 'translateY(-1px)',
  },
  '& .tile-grid .tnum': {
    display: 'block',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    color: 'primary.main',
    mb: 0.5,
  },
  '& .tile-grid .ttitle': {
    display: 'block',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 14,
    fontWeight: 700,
    color: 'text.primary',
    mb: 0.5,
  },
  '& .tile-grid .tdesc': {
    display: 'block',
    fontSize: 13,
    color: 'text.secondary',
    lineHeight: 1.5,
  },

  // .diagram — figure wrapper for SVGs
  '& .diagram': {
    display: 'block',
    width: '100%',
    height: 'auto',
    my: 2.5,
    bgcolor: 'background.elev2',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 1,
  },
  '& .diagram-caption': {
    display: 'block',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    color: 'text.faintest',
    textAlign: 'center',
    mt: -1.5,
    mb: 2,
    letterSpacing: '0.5px',
  },

  // .kbd — keycap
  '& .kbd': {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.8em',
    px: 0.7,
    py: 0.05,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 0.5,
    bgcolor: 'background.elev2',
    color: 'primary.light',
  },

  // .tag — uppercase pill
  '& .tag': {
    display: 'inline-block',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    px: 1,
    py: 0.25,
    mr: 0.5,
    border: '1px solid',
    borderColor: 'primary.strong',
    color: 'primary.main',
    borderRadius: 999,
    bgcolor: 'primary.soft',
  },
  '& .tag.mute': { borderColor: 'divider', color: 'text.faintest', bgcolor: 'transparent' },
} as const;

export interface DocsMarkdownProps {
  text: string;
}

export function DocsMarkdown({ text }: DocsMarkdownProps) {
  return (
    <Box sx={proseWrapperSx}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeHighlight, { detect: true, ignoreMissing: true }],
        ]}
        components={{
          h1: ({ children }: { children?: ReactNode }) => (
            <Typography variant="h1" component="h1" sx={{ mt: 0, mb: 2 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }: { children?: ReactNode }) => (
            <Typography variant="h2" component="h2" sx={{ mt: 5, mb: 1.5, color: 'primary.main' }}>
              {children}
            </Typography>
          ),
          h3: ({ children }: { children?: ReactNode }) => (
            <Typography variant="h3" component="h3" sx={{ mt: 3.5, mb: 1, color: 'text.primary' }}>
              {children}
            </Typography>
          ),
          h4: ({ children }: { children?: ReactNode }) => (
            <Typography variant="subtitle2" component="h4" sx={{ mt: 2.5, mb: 0.5, fontWeight: 700 }}>
              {children}
            </Typography>
          ),
          p: ({ children }: { children?: ReactNode }) => (
            <Typography variant="body1" component="p" sx={{ my: 1.5, lineHeight: 1.7 }}>
              {children}
            </Typography>
          ),
          strong: ({ children }: { children?: ReactNode }) => (
            <Box component="strong" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {children}
            </Box>
          ),
          em: ({ children }: { children?: ReactNode }) => (
            <Box component="em" sx={{ fontStyle: 'italic', color: 'primary.light' }}>
              {children}
            </Box>
          ),
          a: ({ children, href }: { children?: ReactNode; href?: string }) => {
            const isInternal = href?.startsWith('/') ?? false;
            return (
              <Link
                href={href}
                target={isInternal ? undefined : '_blank'}
                rel={isInternal ? undefined : 'noopener noreferrer'}
                underline="hover"
                sx={{ color: 'primary.main', fontWeight: 500 }}
              >
                {children}
              </Link>
            );
          },
          hr: () => <Divider sx={{ my: 4, borderStyle: 'dashed' }} />,
          ul: ({ children }: { children?: ReactNode }) => (
            <Box component="ul" sx={{ my: 1.5, pl: 3, listStyleType: 'disc' }}>
              {children}
            </Box>
          ),
          ol: ({ children }: { children?: ReactNode }) => (
            <Box component="ol" sx={{ my: 1.5, pl: 3, listStyleType: 'decimal' }}>
              {children}
            </Box>
          ),
          li: ({ children }: { children?: ReactNode }) => (
            <Box
              component="li"
              sx={{
                my: 0.5,
                fontSize: '0.875rem',
                lineHeight: 1.65,
                '&::marker': { color: 'text.secondary' },
              }}
            >
              {children}
            </Box>
          ),
          blockquote: ({ children }: { children?: ReactNode }) => (
            <Box
              component="blockquote"
              sx={{
                my: 2,
                pl: 2,
                py: 0.5,
                borderLeft: '3px solid',
                borderColor: 'primary.light',
                color: 'text.secondary',
                bgcolor: 'background.elev2',
              }}
            >
              {children}
            </Box>
          ),
          code: ({ className, children }: { className?: string; children?: ReactNode }) => {
            const isFenced = className?.startsWith('language-') ?? false;
            if (isFenced) return <code className={className}>{children}</code>;
            return (
              <Box component="code" sx={inlineCodeSx}>
                {children}
              </Box>
            );
          },
          pre: ({ children }: { children?: ReactNode }) => (
            <Box component="pre" sx={preWrapperSx}>
              {children}
            </Box>
          ),
          table: ({ children }: { children?: ReactNode }) => (
            <TableContainer
              sx={{
                my: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Table size="small">{children}</Table>
            </TableContainer>
          ),
          thead: ({ children }: { children?: ReactNode }) => (
            <TableHead sx={{ bgcolor: 'background.elev2' }}>{children}</TableHead>
          ),
          tbody: ({ children }: { children?: ReactNode }) => (
            <TableBody>{children}</TableBody>
          ),
          tr: ({ children }: { children?: ReactNode }) => <TableRow>{children}</TableRow>,
          th: ({ children }: { children?: ReactNode }) => (
            <TableCell>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {children}
              </Typography>
            </TableCell>
          ),
          td: ({ children }: { children?: ReactNode }) => (
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                {children}
              </Typography>
            </TableCell>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </Box>
  );
}
